import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { Bot, Send, User } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Should I buy Nvidia today?",
  "Compare Apple and Microsoft.",
  "Which Nigerian bank is stronger?",
  "What ETFs are similar to VOO?",
];

export default function Chat() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm Wealth Builder AI, your investing assistant. Ask me anything about stocks, ETFs, funds or building a portfolio across global markets." },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      const detail = e.response?.data?.detail || "Sorry, something went wrong. Please try again.";
      toast.error(detail);
      setMessages((m) => [...m, { role: "assistant", content: detail }]);
    } finally { setLoading(false); }
  };

  return (
    <main className="mx-auto flex max-w-3xl flex-col px-6 py-8 md:px-8" style={{ minHeight: "calc(100vh - 73px)" }}>
      <div>
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-stone-500"><Bot size={14} strokeWidth={1.5} /> AI Assistant</p>
        <h1 className="mt-2 font-head text-3xl font-medium tracking-tight text-stone-900">Wealth Builder AI</h1>
      </div>

      <div className="mt-5 flex-1 space-y-4 overflow-y-auto rounded-xl border border-stone-200 bg-white p-5" data-testid="chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.role}`}>
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${m.role === "user" ? "bg-stone-900 text-white" : "bg-navy text-white"}`}>
              {m.role === "user" ? <User size={15} strokeWidth={1.5} /> : <Bot size={15} strokeWidth={1.5} />}
            </span>
            <div className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm ${m.role === "user" ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-800"}`}>{m.content}</div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-3" data-testid="chat-loading">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-navy text-white"><Bot size={15} strokeWidth={1.5} /></span>
            <div className="flex items-center gap-1 rounded-2xl bg-stone-100 px-4 py-3">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-stone-400" style={{ animationDelay: "0ms" }} />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-stone-400" style={{ animationDelay: "150ms" }} />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-stone-400" style={{ animationDelay: "300ms" }} />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {messages.length <= 1 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {SUGGESTIONS.map((s, i) => (
            <button key={i} data-testid={`chat-suggestion-${i}`} onClick={() => send(s)}
              className="rounded-full border border-stone-200 bg-white px-3 py-1.5 text-xs font-medium text-stone-600 transition-colors hover:bg-stone-50">{s}</button>
          ))}
        </div>
      )}

      <div className="mt-3 flex gap-3">
        <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Ask about any stock, fund or strategy…"
          className="h-12 flex-1 rounded-lg border border-stone-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20" />
        <button data-testid="chat-send-btn" onClick={() => send()} disabled={loading || !input.trim()}
          className="flex h-12 w-12 items-center justify-center rounded-lg bg-navy text-white transition-colors hover:bg-navyd disabled:opacity-60">
          <Send size={17} strokeWidth={1.5} />
        </button>
      </div>
      <p className="mt-2 text-center text-xs text-stone-400">Wealth Builder AI can make mistakes. This is educational only, not financial advice.</p>
    </main>
  );
}
